import { MutableRefObject, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ErrorBar from '../../components/errorBar/ErrorBar.component';
import PlayerWidget from '../../components/playerWidget/PlayerWidget.component';
import ProgressBar from '../../components/progressBar/progressBar.component';
import ProgressButton from '../../components/progressButton/ProgressButton.component';
import Score from '../../components/score/Score.component';
import { GameContext } from '../../core/context/game.context';
import { pushGtmDataLayer } from '../../core/helper/gtm.helper';
import { safeImageImport } from '../../core/helper/image.helper';
import { getFormatted } from '../../core/helper/string.helper';
import { GameProps } from './game.interface';
import { gameReducer, initState } from './game.reducers';
import './game.style.scss';

export default function Game({ data, onAnswerClick, onQuestionTimeout, onNewRoundClick }: GameProps) {
  const gameCtx = useContext(GameContext);
  const initialGameState = initState(gameCtx);
  const [gameState, gameDispatch] = useReducer(gameReducer, initialGameState);
  const [loquisState, setLoquisState] = useState(data?.loquis);
  const skipCdIntRef = useRef<number>();
  const userReponseCdIntRef = useRef<number>();
  const rndOptionsIds = useRef<number[]>([]);
  const optionClickLockedRef = useRef<boolean>(false);
  const timeoutHandledRef = useRef<boolean>(false);
  const timeoutHandlerRef = useRef<() => void>();
  const loquisDurationRef = useRef<number>();
  const audioTagRef = useRef<HTMLAudioElement>();
  const isPlayerSeekingRef = useRef<boolean>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = gameCtx.getTheme();
  const activeLoquis = loquisState || data?.loquis;
  const fallbackPicture =
    gameCtx.getPreset()?.cardImageUrl ||
    gameCtx.getPreset()?.background ||
    safeImageImport(`assets/images/${gameCtx.values.instance.id}/bg/small/01`);
  timeoutHandlerRef.current = timeoutHandler;

  function stopCountdown(intCodeRef: MutableRefObject<number | undefined>) {
    window.clearInterval(intCodeRef.current);
    intCodeRef.current = undefined;
  }

  useEffect(() => {
    if (gameState.phase === 'waiting-for-user-action') {
      userReponseCdIntRef.current = window.setInterval(() => {
        gameDispatch({ type: 'decrementUserResponseCD' });
      }, 1000);
    }
  }, [gameState.phase]);

  useEffect(() => {
    gameCtx.setScore(gameState.score);
  }, [gameState.score]);

  useEffect(() => {
    if (gameState.skipCountdown === 0) {
      window.clearInterval(skipCdIntRef.current);
    }
  }, [gameState.skipCountdown]);

  useEffect(() => {
    if (gameState.userResponseCD === 0) {
      window.clearInterval(userReponseCdIntRef.current);
      if (gameState.phase === 'waiting-for-user-action' && gameState.noResponse) {
        timeoutHandlerRef.current?.();
      }
    }
  }, [gameState.userResponseCD, gameState.phase, gameState.noResponse]);

  useEffect(() => {
    if (
      gameState.wrongQuestionsCounter >= gameCtx.getPreset().maxWrongQuestions ||
      gameState.answeredQuestionsCounter === gameCtx.getPreset().maxQuestions
    ) {
      stopCountdown(userReponseCdIntRef);
      gameDispatch({
        type: 'setIsEndGame',
        payload: true,
      });
    }
  }, [gameState.wrongQuestionsCounter, gameState.answeredQuestionsCounter]);

  useEffect(() => {
    if (!data) return;

    setLoquisState(data.loquis);
    optionClickLockedRef.current = false;
    timeoutHandledRef.current = false;
    gameDispatch({
      type: 'init',
      payload: {
        progressButtonTO: gameCtx.getPreset().skipTO,
        wrongQuestionsCounter: data.play.number_wrong_answers,
        isEndGame: data.play.is_completed,
        userResponseCD: gameCtx.getPreset().userResponseTO,
        skipCountdown: gameCtx.getPreset().skipTO,
        answerOKIdx: -1,
        correctAnswerIdx: -1,
        answeredQuestionsCounter: Math.max(data.play.current_question_position - 1, 0),
        score: data.play.number_correct_answers * gameCtx.getPreset().stepScore,
        error: [],
      },
    });
    skipCdIntRef.current = undefined;
    rndOptionsIds.current = shuffle();
    return () => {
      window.clearInterval(skipCdIntRef.current);
    };
  }, [data?.question.id]);

  useEffect(
    () => () => {
      window.clearInterval(skipCdIntRef.current);
      window.clearInterval(userReponseCdIntRef.current);
    },
    []
  );

  const isWrongChosenOption = (i: number) => {
    return gameState.phase === 'playing-loquis' && gameState.error.indexOf(i) !== -1;
  };

  const isCorrectAnswer = (i: number) => {
    return gameState.phase === 'playing-loquis' && gameState.correctAnswerIdx === i;
  };

  const isOptionClickDisabled = () => {
    return optionClickLockedRef.current || gameState.phase !== 'waiting-for-user-action' || !gameState.noResponse;
  };

  const advHandler = () => {
    return;
  };

  function newRoundHandler() {
    if (gameState.loquisEnded === false) {
      pushGtmDataLayer('loquisSkipped', { duration: loquisDurationRef.current });
    } else {
      pushGtmDataLayer('loquisNotSkipped', { duration: loquisDurationRef.current });
    }
    onNewRoundClick();
  }

  function shuffle() {
    const optionsCount = Math.min(gameCtx.getPreset().options, data?.question.optionsList?.length ?? 0);
    const array = Array.from({ length: optionsCount }, (_, index) => index);
    let currentIndex = array.length;
    let randomIndex;

    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }

    return array;
  }

  function getVisibleOptionIndexByAnswerId(answerId: string) {
    const visibleOptions = data?.question.optionsList?.filter((v, i) => i < gameCtx.getPreset().options) ?? [];

    return visibleOptions.findIndex((option, i, options) => {
      const optionIndex = rndOptionsIds.current[i] ?? i;
      return (options[optionIndex] ?? option).id === answerId;
    });
  }

  async function chooseOptionHandler(answerId: string, arrId: number) {
    if (isOptionClickDisabled()) return;

    optionClickLockedRef.current = true;
    stopCountdown(userReponseCdIntRef);

    try {
      const answer = await onAnswerClick(answerId);
      const payload = {
        answeredQuestionsCounter: answer.play.current_question_position,
        wrongQuestionsCounter: answer.play.number_wrong_answers,
        score: answer.play.number_correct_answers * gameCtx.getPreset().stepScore,
        isEndGame: answer.play.is_completed,
      };

      setLoquisState(answer.loquis);

      if (answer.is_correct) {
        pushGtmDataLayer('questionOK');
        gameDispatch({
          type: 'questionOK',
          payload: {
            ...payload,
            answerOKIdx: arrId,
          },
        });
      } else {
        pushGtmDataLayer('questionKO');
        gameDispatch({
          type: 'questionKO',
          payload: {
            ...payload,
            newError: arrId,
            correctAnswerIdx: getVisibleOptionIndexByAnswerId(answer.correct_answer_id),
          },
        });
      }
    } catch (e) {
      optionClickLockedRef.current = false;
      console.error(e);
    }
  }

  async function timeoutHandler() {
    if (!data || timeoutHandledRef.current || optionClickLockedRef.current) {
      return;
    }

    timeoutHandledRef.current = true;
    optionClickLockedRef.current = true;

    try {
      const answer = await onQuestionTimeout();
      const payload = {
        answeredQuestionsCounter: answer.play.current_question_position,
        wrongQuestionsCounter: answer.play.number_wrong_answers,
        score: answer.play.number_correct_answers * gameCtx.getPreset().stepScore,
        isEndGame: answer.play.is_completed,
      };

      setLoquisState(answer.loquis);
      pushGtmDataLayer('questionKO');
      gameDispatch({
        type: 'questionTimeout',
        payload: {
          ...payload,
          correctAnswerIdx: getVisibleOptionIndexByAnswerId(answer.correct_answer_id),
        },
      });
    } catch (e) {
      optionClickLockedRef.current = false;
      timeoutHandledRef.current = false;
      console.error(e);
    }
  }

  function playerInitHandler(e: HTMLAudioElement) {
    audioTagRef.current = e;
  }

  function playHandler(e: any) {
    if (!isPlayerSeekingRef.current && e.target.played.length === 0) {
      pushGtmDataLayer('loquisPlay');
    } else {
      isPlayerSeekingRef.current = false;
    }
    startSkipCountdown();
  }

  function pauseHandler(e: any) {
    stopCountdown(skipCdIntRef);
    isPlayerSeekingRef.current = e.target.seeking;
  }

  function audioEndHandler(e: any) {
    if (gameState.phase === 'playing-loquis') {
      gameDispatch({ type: 'loquisEnd' });
    } else if (gameState.phase === 'playing-adv') {
      if (gameState.isEndGame) {
        gotoSummary();
      } else {
        onNewRoundClick();
      }
    }
  }

  function startSkipCountdown() {
    if (typeof skipCdIntRef.current !== 'undefined') return;
    gameDispatch({ type: 'decrementSkipCountdown' });
    skipCdIntRef.current = window.setInterval(() => {
      gameDispatch({ type: 'decrementSkipCountdown' });
    }, 1000);
  }

  function gotoSummary() {
    const maxScore = gameCtx.getPreset().stepScore * gameCtx.getPreset().maxQuestions;
    if (gameState.score < maxScore) {
      pushGtmDataLayer('notMaxScore', { score: gameState.score, maxScore });
    } else {
      pushGtmDataLayer('maxScore', { score: gameState.score, maxScore });
    }
    navigate(
      `/${gameCtx.getLanguage()}/${gameCtx.getInstance().id}/${gameCtx.getInstance().name}/${gameCtx.getPreset().id}/${gameCtx.getPreset().name}/summary`
    );
  }

  function getSkipCountdownText() {
    return (
      '00:' +
      gameState.skipCountdown.toLocaleString('it-IT', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })
    );
  }

  const goNext = (cb: Function) => {
    if (gameState.pendingAdv) {
      gameDispatch({ type: 'playAdv' });
    } else {
      cb();
    }
  };

  return (
    <section className="GameComponent" style={{ backgroundColor: theme.main[4] }}>
      <div className="container">
        <div style={{ flexGrow: 0 }}>
          <img
            className="container__logo sm-hide"
            src={
              gameCtx.getPreset()?.logo ||
              safeImageImport(`assets/images/${gameCtx.values.instance.id}/loghi/logo-block`)
            }
            alt="loquis-challenge-logo"
            width="220"
          />
          <img
            className="container__logo sm-show md-hide lg-hide xl-hide"
            src={
              gameCtx.getPreset()?.logo ||
              safeImageImport(`assets/images/${gameCtx.values.instance.id}/loghi/logo-inline`)
            }
            alt="loquis-challenge-logo"
          />
        </div>
        {data ? (
          <>
            <div
              className={'container__header ' + (gameState.phase === 'playing-adv' ? 'container__header--adv' : '')}
              style={{ flexGrow: 0 }}
            >
              <button
                className="basicButton basicButton--light light basicButton--sm sm-show hide"
                style={{ color: theme.main[0], backgroundColor: theme.main[2] }}
                onClick={gotoSummary}
              >
                {t('game.end')}
              </button>
              {(gameState.phase === 'playing-loquis' || gameState.phase === 'playing-adv') && activeLoquis && (
                <div className="container__header__player">
                  <PlayerWidget
                    loquisId={activeLoquis.id}
                    localeId={activeLoquis.localeId}
                    name={activeLoquis.picture.title}
                    channel={activeLoquis.channel}
                    picture={activeLoquis.picture.url_360}
                    play
                    playAdv={gameState.phase === 'playing-adv'}
                    onPlay={playHandler}
                    onPause={pauseHandler}
                    onEnded={audioEndHandler}
                    onDurationChange={(e) => (loquisDurationRef.current = e.target.duration)}
                    onPlayerInit={playerInitHandler}
                    onAdv={advHandler}
                  />
                </div>
              )}
              <picture>
                <source srcSet={activeLoquis?.picture.url_720 || fallbackPicture} media="(min-width: 575.98px)" />
                <img
                  className="container__header__picture"
                  src={activeLoquis?.picture.url_360 || fallbackPicture}
                  alt=""
                />
              </picture>
              <p className="container__header__urCountdown">{getFormatted(gameState.userResponseCD)}</p>
            </div>
            {gameState.phase === 'playing-adv' ? (
              <>
                <p className="container__advLabel">{t('game.advertising')}</p>
                <div className="container__adv" style={{ backgroundColor: theme.variants[0][0] }}>
                  <h3>{gameState.pendingAdv.banner_title}</h3>
                  <p>{gameState.pendingAdv.banner_description}</p>
                </div>
              </>
            ) : (
              <>
                <div
                  className={
                    'container__question' + (gameState.userResponseCD === 0 ? ' container__question--answered' : '')
                  }
                  style={{ backgroundColor: theme.variants[0][0] }}
                >
                  <p>{data.question.text}</p>
                </div>
                <ul className="container__options">
                  {data.question.optionsList
                    ?.filter((v, i) => i < gameCtx.getPreset().options)
                    .map((option, i, a) => {
                      const optionIndex = rndOptionsIds.current[i] ?? i;
                      const currentOption = a[optionIndex] ?? option;

                      return (
                        <li
                          key={i}
                          className={
                            'container__options__opt' +
                            (isWrongChosenOption(i) ? ' container__options__opt--error' : '') +
                            (gameState.phase === 'playing-loquis' ? ' container__options__opt--void' : '') +
                            (gameState.phase === 'playing-loquis' && gameState.correctAnswerIdx === i
                              ? ' container__options__opt__correctOne'
                              : '') +
                            (gameState.phase === 'playing-loquis' && gameState.userResponseCD === 0 ? ' timed-out' : '')
                          }
                          style={{
                            backgroundColor: isWrongChosenOption(i)
                              ? theme.main[3]
                              : isCorrectAnswer(i)
                                ? theme.variants[1][0]
                                : null,
                            borderColor: isWrongChosenOption(i)
                              ? theme.main[3]
                              : isCorrectAnswer(i)
                                ? theme.variants[1][0]
                                : null,
                          }}
                          onClick={() => chooseOptionHandler(currentOption.id, i)}
                        >
                          <span>{currentOption.text}</span>
                        </li>
                      );
                    })}
                </ul>
                <div
                  className={
                    'container__userData ' +
                    (gameState.phase === 'playing-loquis' ? 'container__userData--playing' : '')
                  }
                >
                  <div className="container__userData__score">
                    <Score partial={gameState.score / gameCtx.getPreset().stepScore} />
                  </div>
                  <div className="container__userData__progress">
                    <ProgressBar progress={gameState.answeredQuestionsCounter} max={gameCtx.getPreset().maxQuestions} />
                  </div>
                  <div className="container__userData__errors">
                    <ErrorBar errors={gameState.wrongQuestionsCounter} />
                  </div>
                  <div>
                    <button
                      className="basicButton w-full mt-1 xs-hide"
                      style={{ color: theme.main[0], backgroundColor: theme.main[2] }}
                      onClick={gotoSummary}
                    >
                      {t('game.end')}
                    </button>
                  </div>
                </div>
              </>
            )}
            {gameState.phase === 'playing-loquis' && (
              <div
                className={
                  'container__footer' +
                  (gameState.error.length !== 0 || (gameState.answerOKIdx === -1 && gameState.userResponseCD === 0)
                    ? ' container__footer--ko'
                    : '') +
                  (gameState.answerOKIdx !== -1 ? ' container__footer--ok' : '')
                }
                style={{ backgroundColor: gameState.answerOKIdx !== -1 ? theme.variants[1][1] : theme.variants[3][0] }}
              >
                {
                  // wrong answer
                  gameState.error.length !== 0 ? (
                    <div className="container__footer__ko">
                      {t('game.ko-answ')}
                      <br />
                      {!gameState.isEndGame ? (
                        <ProgressButton
                          state="ko"
                          paused={false}
                          text={getSkipCountdownText()}
                          endText={t('game.go-on')}
                          timeout={gameState.progressButtonTO}
                          onClickHandler={() => goNext(newRoundHandler)}
                        />
                      ) : null}
                      {gameState.isEndGame ? (
                        <ProgressButton
                          state="ko"
                          paused={false}
                          text={getSkipCountdownText()}
                          endText={t('game.go-summ')}
                          timeout={gameState.progressButtonTO}
                          onClickHandler={() => goNext(gotoSummary)}
                        />
                      ) : null}
                    </div>
                  ) : null
                }
                {
                  // correct answer
                  gameState.answerOKIdx !== -1 ? (
                    <div className="container__footer__ok">
                      {t('game.ok-answ')}
                      <br />

                      {!gameState.isEndGame ? (
                        <ProgressButton
                          state="ok"
                          paused={false}
                          text={getSkipCountdownText()}
                          endText={t('game.go-on')}
                          timeout={gameState.progressButtonTO}
                          onClickHandler={() => goNext(newRoundHandler)}
                        />
                      ) : null}

                      {gameState.isEndGame ? (
                        <ProgressButton
                          state="ok"
                          paused={false}
                          text={getSkipCountdownText()}
                          endText={t('game.go-summ')}
                          timeout={gameState.progressButtonTO}
                          onClickHandler={() => goNext(gotoSummary)}
                        />
                      ) : null}
                    </div>
                  ) : // no answer
                  gameState.error.length === 0 && gameState.userResponseCD === 0 ? (
                    <div className="container__footer__ko">
                      {t('game.timeout')}
                      <br />
                      {!gameState.isEndGame ? (
                        <button
                          className="basicButton"
                          style={{ backgroundColor: theme.main[0], color: theme.variants[3][0] }}
                          onClick={() => goNext(newRoundHandler)}
                        >
                          {t('game.go-on')}
                        </button>
                      ) : null}
                      {gameState.isEndGame ? (
                        <button
                          className="basicButton"
                          style={{ backgroundColor: theme.main[0], color: theme.variants[3][0] }}
                          onClick={() => goNext(gotoSummary)}
                        >
                          {t('game.go-summ')}
                        </button>
                      ) : null}
                    </div>
                  ) : null
                }
              </div>
            )}
          </>
        ) : null}
      </div>
    </section>
  );
}
